import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { Calendar, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { toYmd, summarizeIqpNames } from '../utils/orderInspectionJobs';

function tomorrowYmd() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toYmd(d.toISOString()) || d.toISOString().slice(0, 10);
}

/**
 * Pick a visit date, OK ??create/assign jobs to each SS default IQP
 * and put them on that IQP's dashboard + calendar.
 */
export default function ScheduleToIqpModal({
  buildingId,
  buildingName,
  items = [],
  onClose,
}) {
  const queryClient = useQueryClient();
  const earliestDue = useMemo(() => {
    const dates = (items || []).map((i) => toYmd(i.due_date || i.nextDueDate)).filter(Boolean).sort();
    return dates[0] || tomorrowYmd();
  }, [items]);
  const [scheduledDate, setScheduledDate] = useState(earliestDue);
  const iqpLabel = summarizeIqpNames(items).join(', ') || 'Default IQP';
  const ssCodes = [...new Set((items || []).map((i) => i.ss_code || i.ssCode).filter(Boolean))];

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/jobs/schedule-to-default-iqp', {
        buildingId,
        scheduled_date: scheduledDate,
        ssCodes,
        items: (items || []).map((i) => ({
          ss_code: i.ss_code || i.ssCode,
          due_date: toYmd(i.due_date || i.nextDueDate) || scheduledDate,
        })),
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs', 'building', String(buildingId)]);
      queryClient.invalidateQueries(['buildings']);
      queryClient.invalidateQueries(['schedules']);
      queryClient.invalidateQueries(['job-schedule-events']);
      const n = data?.scheduled || 0;
      const skip = data?.skipped || 0;
      if (n) {
        toast.success(
          `${n} SS scheduled to ${iqpLabel || 'default IQP'} ??they will see it on the app dashboard`
        );
      } else {
        toast('Nothing scheduled. Set an internal default IQP on the SS first.');
      }
      if (skip) {
        toast(`${skip} SS skipped (no default IQP worker)`, { icon: '?�️' });
      }
      onClose?.(data);
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to schedule'),
  });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" onClick={() => onClose?.()}>
      <div
        className="bg-card rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-600" />
              Schedule for default IQP
            </h2>
            <p className="text-sm text-gray-600 mt-1">{buildingName || 'Building'}</p>
          </div>
          <button type="button" onClick={() => onClose?.()} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700">
            Pick a visit date, then <strong>OK</strong>. Each SS is assigned to its default IQP and appears on that
            IQP&apos;s app <strong>Dashboard</strong> (Scheduled) and calendar.
          </p>
          <p className="text-sm">
            <span className="text-gray-500">IQP: </span>
            <span className="font-medium text-gray-900">{iqpLabel}</span>
            <span className="text-gray-500"> · {ssCodes.length} SS</span>
          </p>
          <label className="block text-sm font-medium text-gray-700">
            Visit date
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={mutation.isPending || !scheduledDate || !ssCodes.length}
              onClick={() => mutation.mutate()}
              className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
